import React from 'react';
import { motion } from 'framer-motion';
import { 
  FiCode, 
  FiTool, 
  FiServer, 
  FiPackage, 
  FiCheckSquare,
  FiZap,
  FiSearch,
  FiClipboard,
  FiCpu,
  FiLayers
} from 'react-icons/fi';
import { Skill } from '../types/resume.types';

interface SkillsProps {
  skills: Skill[];
}

const categoryIcons: Record<string, React.ElementType> = {
  'Programming Languages': FiCode,
  'Test Automation Frameworks': FiTool,
  'API Testing': FiServer,
  'Performance Testing': FiZap,
  'CI/CD & DevOps': FiPackage,
  'Analysis & Debugging': FiSearch,
  'Testing Methodologies': FiCheckSquare,
  'AI & Emerging Tech': FiCpu,
  'Project Management': FiClipboard,
  'Tools & Platforms': FiLayers
};

const categoryColors: Record<string, string> = {
  'Programming Languages': 'from-purple-400 to-purple-600',
  'Test Automation Frameworks': 'from-blue-400 to-blue-600',
  'API Testing': 'from-green-400 to-green-600',
  'Performance Testing': 'from-yellow-400 to-orange-500',
  'CI/CD & DevOps': 'from-orange-400 to-orange-600',
  'Analysis & Debugging': 'from-red-400 to-red-600',
  'Testing Methodologies': 'from-pink-400 to-pink-600',
  'AI & Emerging Tech': 'from-cyan-400 to-cyan-600',
  'Project Management': 'from-indigo-400 to-indigo-600',
  'Tools & Platforms': 'from-teal-400 to-teal-600'
};

const Skills: React.FC<SkillsProps> = ({ skills }) => {
  return (
    <section id="skills" className="py-16 section-padding">
      <div className="container-width">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Technical Skills
          </h2>
          <p className="text-gray-600 dark:text-gray-300 text-lg max-w-2xl mx-auto">
            Comprehensive expertise in automation testing tools, programming languages, and methodologies
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-500 to-secondary-500 mx-auto mt-4"></div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {skills.map((skill, index) => {
            const Icon = categoryIcons[skill.category] || FiCode;
            const gradientColor = categoryColors[skill.category] || 'from-gray-400 to-gray-600';
            
            return (
              <motion.div
                key={skill.category}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 card-shadow hover:shadow-xl transition-shadow"
              >
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${gradientColor} flex items-center justify-center mb-4`}>
                  <Icon className="text-white" size={24} />
                </div>
                
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                  {skill.category}
                </h3>
                
                <div className="flex flex-wrap gap-2">
                  {skill.items.map((item, itemIndex) => (
                    <motion.span
                      key={item}
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      transition={{ duration: 0.3, delay: index * 0.1 + itemIndex * 0.05 }}
                      viewport={{ once: true }}
                      className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      {item}
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Skills;